import React, { FC, useCallback } from 'react';
import { Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  setDeletingStudentError, deleteStudent,
} from '../slice/actions';
import {
  getDeletingStudentError,
  getIsDeletingStudent,
} from '../slice/selectors';
import classes from './DeleteStudentModal.module.less';
import AppModal, { ModalButtons } from '../../../components/AppModal';
import { success, error } from '../../../components/Message';
import Text from '../../../components/Text';
import { polishServerText } from '../../../utils/apiErrors';
import { UserData } from '../../../services/ApiService.types';
import useActionStatus from '../../../hooks/useActionStatus';

type DeleteStudentModalProps = {
  student: UserData;
  hideModal: () => void;
};

const DeleteStudentModal: FC<DeleteStudentModalProps> = ({ student, hideModal }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isLoading = useSelector(getIsDeletingStudent);
  const apiError = useSelector(getDeletingStudentError);

  const onSuccess = useCallback(() => {
    success(t('student:delete.messages.success'));
    hideModal();
  }, [hideModal, t]);

  const onError = useCallback(() => {
    error(t('student:delete.messages.error'));
  }, [t]);

  useActionStatus(onSuccess, onError, {
    isLoadingSelector: getIsDeletingStudent,
    errorSelector: getDeletingStudentError,
  });

  const onClick = useCallback(() => {
    if (student && student.id) {
      dispatch(deleteStudent(student.id));
    }
  }, [dispatch, student]);

  const onCancel = useCallback(() => {
    dispatch(setDeletingStudentError(null));
    hideModal();
  }, [dispatch, hideModal]);

  return student && (
    <AppModal title={t('student:delete.title')} visible={Boolean(student)} onCancel={onCancel}>
      <Text className={classes.description}>{t('student:delete.description')}</Text>
      <ModalButtons>
        <Button
          type="ghost"
          onClick={onCancel}
          disabled={isLoading}
        >
          {t('student:delete.cancel')}
        </Button>
        <Button
          type="primary"
          danger
          onClick={onClick}
          disabled={isLoading}
          loading={isLoading}
        >
          {t('student:delete.submit')}
        </Button>
      </ModalButtons>
      {/* Tekst backendu tylko przy odpowiedzi z kodem — inne błędy zgłasza samo powiadomienie */}
      {apiError?.code && polishServerText(apiError.message) && <div>{apiError.message}</div>}
    </AppModal>
  );
};

export default DeleteStudentModal;
